import { useState } from "react";
import { motion } from "framer-motion";
import { Reply, SmilePlus, Pin, Shield } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { UserAvatar } from "@/components/Avatar";
import { mediaUrl } from "@/lib/chatApi";

const QUICK = ["👍", "❤️", "😂", "🎉", "😮", "😢"];

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

function renderContent(text, currentUser, mine) {
  const parts = text.split(/(@[A-Za-z0-9_]+)/g);
  return parts.map((p, i) => {
    if (p.startsWith("@")) {
      const me = p.slice(1) === currentUser;
      if (me) {
        return (
          <span key={i} className="font-bold px-1 rounded bg-amber-300/50 text-amber-900 dark:bg-amber-400/25 dark:text-amber-200">
            {p}
          </span>
        );
      }
      return (
        <span key={i} className={`font-semibold ${mine ? "text-indigo-100 underline decoration-indigo-300/50" : "text-indigo-600 dark:text-indigo-300"}`}>
          {p}
        </span>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

export default function MessageBubble({ message, mine, currentUser, onReply, onReact, onPin }) {
  const [reactOpen, setReactOpen] = useState(false);
  const img = mediaUrl(message.image_url);
  const reactions = message.reactions || {};
  const hasReactions = Object.keys(reactions).length > 0;

  const react = (emoji) => {
    onReact(message.id, emoji);
    setReactOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      data-testid={`chat-message-item-${message.id}`}
      className={`group flex gap-3 ${mine ? "flex-row-reverse" : "flex-row"}`}
    >
      <UserAvatar username={message.username} avatar={message.avatar} size={36} />
      <div className={`flex flex-col max-w-[75%] md:max-w-[60%] ${mine ? "items-end" : "items-start"}`}>
        <div className="flex items-center gap-2 mb-1 px-1">
          <span className={`text-xs font-semibold ${
            message.is_admin
              ? "text-amber-600 dark:text-amber-400"
              : mine ? "text-indigo-600 dark:text-indigo-300" : "text-emerald-600 dark:text-emerald-400"
          }`}>
            {mine ? "Kamu" : message.username}
          </span>
          {message.is_admin && (
            <span data-testid="admin-badge" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40">
              <Shield className="w-2.5 h-2.5" /> Admin
            </span>
          )}
          <span className="text-[10px] font-medium opacity-60 text-slate-500 dark:text-slate-400">{fmtTime(message.created_at)}</span>
        </div>

        <div className={`flex items-center gap-1.5 ${mine ? "flex-row-reverse" : "flex-row"}`}>
          <div
            className={`p-3.5 ${
              message.is_admin
                ? (mine
                    ? "bg-amber-600 text-white rounded-2xl rounded-tr-sm shadow-md shadow-amber-950/20 ring-1 ring-amber-300/50"
                    : "bg-amber-50 dark:bg-amber-500/10 text-slate-800 dark:text-amber-50 border border-amber-300 dark:border-amber-500/40 rounded-2xl rounded-tl-sm shadow-sm")
                : (mine
                    ? "bg-indigo-600 text-white rounded-2xl rounded-tr-sm shadow-md shadow-indigo-950/20"
                    : "bg-white dark:bg-slate-800/90 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700/50 rounded-2xl rounded-tl-sm shadow-sm")
            }`}
          >
            {message.reply_to_id && (
              <div
                className={`mb-2 pl-2.5 py-1 border-l-2 rounded text-xs ${
                  mine
                    ? "border-indigo-300/60 bg-indigo-700/40"
                    : "border-emerald-400/60 bg-slate-100 dark:bg-slate-900/50"
                }`}
              >
                <p className="font-semibold text-emerald-600 dark:text-emerald-300/90">{message.reply_to_username}</p>
                <p className="opacity-70 truncate max-w-[220px]">{message.reply_to_text || "📷 Gambar"}</p>
              </div>
            )}
            {img && (
              <img src={img} alt="lampiran" className="max-w-xs md:max-w-sm rounded-xl mb-2 border border-slate-200 dark:border-slate-700/40" />
            )}
            {message.text && (
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                {renderContent(message.text, currentUser, mine)}
              </p>
            )}
          </div>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              data-testid={`reply-button-${message.id}`}
              onClick={() => onReply(message)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <Reply className="w-4 h-4" />
            </button>
            {onPin && (
              <button
                data-testid={`pin-button-${message.id}`}
                onClick={() => onPin(message)}
                title={message.pinned ? "Lepas sematan" : "Sematkan pesan"}
                className={`p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${
                  message.pinned ? "text-indigo-500" : "text-slate-400 hover:text-indigo-500"
                }`}
              >
                <Pin className={`w-4 h-4 ${message.pinned ? "fill-indigo-500" : ""}`} />
              </button>
            )}
            <Popover open={reactOpen} onOpenChange={setReactOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid={`react-button-${message.id}`}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <SmilePlus className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" align={mine ? "end" : "start"} className="z-[10000] p-1.5 w-auto bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <div className="flex gap-1">
                  {QUICK.map((e) => (
                    <button
                      key={e}
                      data-testid={`quick-react-${e}`}
                      onClick={() => react(e)}
                      className="text-xl p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {hasReactions && (
          <div className={`flex gap-1 mt-1.5 flex-wrap ${mine ? "justify-end" : "justify-start"}`}>
            {Object.entries(reactions).map(([emoji, users]) => {
              const active = users.includes(currentUser);
              return (
                <button
                  key={emoji}
                  data-testid={`reaction-chip-${message.id}-${emoji}`}
                  onClick={() => onReact(message.id, emoji)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors ${
                    active
                      ? "bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-500/20 dark:border-indigo-500/50 dark:text-indigo-200"
                      : "bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200 dark:bg-slate-800/70 dark:border-slate-700/60 dark:text-slate-300 dark:hover:bg-slate-800"
                  }`}
                >
                  <span>{emoji}</span>
                  <span className="font-semibold">{users.length}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}
